<template>
  <loading-slice v-if="loading" />
  <div
    v-else-if="productDocument"
    class="product"
    :class="`product--${productDocument.status}`"
  >
    <purchase-module
      :product-document="productDocument"
      :shopify-product="shopifyProduct"
      :product-updated="productUpdated"
      :with-testimonials="
        productDocument.rating_average !== null &&
        productDocument.rating_number !== null
      "
    />

    <blocks
      v-if="productDocument.blocks"
      :blocks="productDocument.blocks"
      :entities="{
        productDocument,
      }"
      :origin="ORIGIN.PRODUCT"
      :nav="!productDocument.navbarHidden"
    />
  </div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex'

import PurchaseModule from '~/slices/PurchaseModule/PurchaseModule'
import ScrollTrackingMixin from '@/mixins/ScrollTrackingMixin'
import LoadingSlice from '@/components/loading/LoadingSlice'
import Blocks from '@/components/blocks/Blocks'

import services from '@/services'

import Product from '@/entities/shopify/Product'

import { ORIGIN } from '@/const'
import { EVENTS } from '@/const/events'
import { instianciateProduct } from '@/entities/sanity/product'

export default {
  name: 'Product',
  components: {
    LoadingSlice,
    PurchaseModule,
    Blocks,
  },
  mixins: [ScrollTrackingMixin],
  async asyncData({
    params,
    $logError,
    i18n,
    error,
    payload,
    $prepareUidForContext,
  }) {
    try {
      let productDocument
      if (payload && payload.productDocument) {
        productDocument = instianciateProduct(payload.productDocument)
      } else {
        // Query to get post content
        const uid = $prepareUidForContext(params.uid)

        productDocument = await services.sanityService
          .getProduct({
            uid,
            lang: i18n.locale.slice(0, 2),
          })
          .then((res) => {
            if (res.length > 0) {
              return instianciateProduct(res[0])
            }

            return null
          })

        if (!productDocument) {
          throw new Error('product-not-found')
        }
      }

      let shopifyProductData
      if (payload && payload.shopify) {
        shopifyProductData = payload.shopify
      } else {
        const shopifyProductID = productDocument.shopify_product.id
        if (shopifyProductID) {
          shopifyProductData = await services.productService
            .getShopifyProduct(shopifyProductID)
            .then((result) => {
              if (result.data.errors) {
                throw new Error(result.data.errors[0].message)
              }

              if (!result.data.data.node) {
                throw new Error('product-not-found')
              }
              return result.data.data.node
            })
        }
      }

      // Returns data to be used in template
      return {
        productDocument,
        shopifyProductData,
        loading: false,
      }
    } catch (e) {
      // Returns error page
      if (e.message !== 'product-not-found') {
        $logError.captureException(e)
      }
      error({ statusCode: 404, message: 'Page not found' })
    }
  },
  data() {
    return {
      loading: true,
      productDocument: null,
      shopifyProductData: null,
      shopifyProduct: null,
      inventoryLinksNumber: 0,
      productUpdated: false,
      isSubscribed: false,
      timeout: null,
      ORIGIN,
    }
  },
  head() {
    if (this.productDocument) {
      const route = `${
        process.env.BASE_URL ? process.env.BASE_URL : 'https://www.asphalte.com'
      }${this.$nuxt.$route.path}`

      const head = services.pageService.getHead(
        {
          type: 'product',
          title: this.productDocument.title,
          route,
          defaultTitle: this.$t('seo.defaultTitle'),
          defaultDesc: this.$t('seo.defaultDesc'),
          sanity: this.productDocument,
          product: this.productDocument.shopify_product,
        },
        this.$generateAlternateLinksMetaSanity(
          this.productDocument._type,
          this.productDocument.alternate_languages
        )
      )

      head.bodyAttrs = {
        class: 'header-transparent-mobile',
      }

      return head
    }

    return {}
  },

  computed: {
    ...mapGetters({
      authenticated: 'auth/authenticated',
      customer: 'auth/customer',
      account: 'subscriptions/account',
      connectedEmail: 'subscriptions/connectedEmail',
      isSubscriptionsLoading: 'subscriptions/isLoading',
      isSubscriptionsFetch: 'subscriptions/onFetch',
      campaigns: 'abtest/campaigns',
      isCampaignsLoading: 'abtest/isLoading',
    }),
  },
  watch: {
    async isCampaignsLoading(oldV, newV) {
      if (oldV !== newV) {
        await this.handleAlternativeSliders()
      }
    },

    isSubscriptionsLoading() {
      this.isSubscribed = this.account.isSubscribed(
        this.$prepareUidForContext(this.$route.params.uid)
      )
    },
    isSubscriptionsFetch() {
      this.isSubscribed = this.account.isSubscribed(
        this.$prepareUidForContext(this.$route.params.uid)
      )
    },
  },
  beforeDestroy() {
    this.deleteMorphoSelected(
      this.$prepareUidForContext(this.$route.params.uid)
    )
    this.setFixHeader(true)

    if (this.timeout) {
      clearTimeout(this.timeout)
    }
  },
  created() {
    this.initProduct()
    if (!this.isCampaignsLoading) {
      this.handleAlternativeSliders()
    }
    this.loading = false
  },
  async mounted() {
    this.$track.page(this.$metaInfo, this.$route, this.$nuxt.context.from)

    this.setFixHeader(false)
    if (this.productDocument && this.shopifyProduct) {
      const result = await services.productService
        .refreshShopifyProduct(this.shopifyProduct.id, this.$i18n.locale)
        .then((r) => {
          if (r.status === 200) {
            return r.data.data.node
          }

          this.$logError.captureException(r)

          return null
        })

      if (result) {
        this.shopifyProduct.update(result)
        // await this.refreshAdditionnalInventory()
        this.$nuxt.$emit(EVENTS.PRODUCT_UPDATED)
        this.productUpdated = true
      }
    }

    if (
      this.connectedEmail === null ||
      (this.connectedEmail !== '' && this.account.email === null)
    ) {
      this.$nuxt.$on(EVENTS.USER_STATE_LOADED, (_) => {
        this.checkSubscription()
      })
    } else {
      this.checkSubscription()
    }

    this.initUserMoreThanOneMinutesOnPage()
  },
  methods: {
    ...mapMutations({
      deleteMorphoSelected: 'behavior/DELETE_MORPHO_SELECTED',
      setFixHeader: 'behavior/SET_FIX_HEADER',
    }),
    initProduct() {
      if (this.productDocument) {
        this.productDocument = instianciateProduct(this.productDocument)
      }
      if (this.shopifyProductData) {
        this.shopifyProduct = new Product(this.shopifyProductData)
      }
    },
    async handleAlternativeSliders() {
      const uid = this.productDocument.uid
      if (this.campaigns.hasProductSliderTest(uid)) {
        const productCampaigns = this.campaigns.getProductSliderTest(uid)
        for (const campaign of productCampaigns) {
          if (!campaign.isOriginal) {
            await this.loadABTestSlider(
              campaign.color,
              campaign.alternateSliderId
            )
          }
        }
      }
    },
    async loadABTestSlider(color, sliderId) {
      const sliderDocument = await services.sanityService.getSliderById({
        id: sliderId,
      })

      if (sliderDocument) {
        const productColor = this.productDocument.colors.find(
          (c) => c.name === color
        )
        if (productColor) {
          productColor.slider = sliderDocument
        }
      }
    },
    checkSubscription() {
      if (this.connectedEmail !== '') {
        this.isSubscribed = this.account.isSubscribed(
          this.$prepareUidForContext(this.$route.params.uid)
        )
      }
    },
    initUserMoreThanOneMinutesOnPage() {
      if (this.timeout === null) {
        this.timeout = setTimeout(() => {
          this.hasSpendOneMinuteOnProductPage()
          clearTimeout(this.timeout)
          this.timeout = null
        }, 60000)
      }
    },
    hasSpendOneMinuteOnProductPage() {
      if (this.authenticated) {
        return services.laPosteService.hasSpendOneMinuteOnProductPage(
          this.customer.email,
          this.productDocument.uid,
          this.$i18n.locale.slice(0, 2)
        )
      }

      if (this.connectedEmail && this.connectedEmail !== '') {
        return services.laPosteService.hasSpendOneMinuteOnProductPage(
          this.connectedEmail,
          this.productDocument.uid,
          this.$i18n.locale.slice(0, 2)
        )
      }
    },
  },
}
</script>
